/**
 * SIL-1351 - the My Activity card. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.6;
 * layout from docs/exchange/design-handoff/README.md section 7 and the
 * `activitySections` markup in the handoff prototype.
 *
 * Purely presentational and deliberately data-agnostic: Orders rows come from
 * `/api/client/transactions` and Offers rows from `/api/client/offers`, two
 * unrelated DTOs. Giving the card a row *shape* instead of a row *type* is
 * what lets one component render both without a discriminated union that
 * would have to grow again for expressed interest in slice 4.
 *
 * `title` is a `ReactNode`, not a string, so the caller supplies the
 * `<Link>`. TanStack Router types route paths and params literally; a
 * `linkTo: string` prop here would have thrown that checking away for every
 * row on the page.
 */
import type { ReactNode } from 'react';
import { Card } from '@siliconx/shared/ui/Card';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { formatDate } from '../../../lib/format';
import type { ActivityCardCopy } from '../copy/activity';
import { ROW_AMOUNT_LABEL, ROW_DATE_LABEL, ROW_ID_LABEL } from '../copy/activity';

export interface ActivityRowData {
  /** React key. The row's real primary key, not the truncated display form. */
  key: string;
  /** Short reference shown in the mono first column. */
  displayId: string;
  /** Row title - the caller passes a `<Link>` when the listing is viewable. */
  title: ReactNode;
  /** Second line under the title: listing id, site, condition band. */
  meta: ReactNode;
  /** `null` renders an em dash (expressed-interest rows carry no amount). */
  amountUsd: number | null;
  /** Optional qualifier under the amount, e.g. the original offer price. */
  amountNote?: ReactNode;
  /** Status badge. Countered offers pass the warn badge plus their actions. */
  status: ReactNode;
  /** Row actions, rendered beside the badge. Countered offers only, today. */
  actions?: ReactNode;
  /** ISO timestamp, or `null` when the row has no date yet. */
  date: string | null;
  /** Optional full-width panel under the row (SIL-2218: the buyer deposit area). */
  detail?: ReactNode;
}

/**
 * Column template matches the handoff: reference | title+meta | amount |
 * status+actions | date, with a min width so the card scrolls horizontally
 * rather than crushing the title column on a narrow viewport.
 */
const ROW_GRID =
  'grid min-w-[780px] grid-cols-[110px_minmax(180px,1fr)_150px_minmax(190px,auto)_110px] items-center gap-3 border-t border-border p-3';

function ActivityRow({ row }: { row: ActivityRowData }) {
  return (
    <>
    <div className={ROW_GRID} data-testid="activity-row">
      <span className="font-num text-xs text-muted-foreground" aria-label={ROW_ID_LABEL}>
        {row.displayId}
      </span>

      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{row.title}</div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">{row.meta}</div>
      </div>

      <div className="flex flex-col" aria-label={ROW_AMOUNT_LABEL}>
        <UsdCell value={row.amountUsd} className="text-left text-sm" />
        {row.amountNote != null && (
          <span className="text-xs text-muted-foreground">{row.amountNote}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {row.status}
        {row.actions}
      </div>

      <span className="text-right text-xs text-muted-foreground" aria-label={ROW_DATE_LABEL}>
        {row.date ? formatDate(row.date) : '—'}
      </span>
    </div>
    {row.detail != null && (
      <div className="min-w-[780px] px-3 pb-3" data-testid="activity-row-detail">
        {row.detail}
      </div>
    )}
    </>
  );
}

/**
 * A single activity card: title, mono count, one-line description, then rows
 * or the empty state.
 *
 * The count is rendered even when zero - it is the card's only "we looked and
 * there is nothing" signal, and hiding it would make an empty card
 * indistinguishable from one still loading.
 */
export function ActivityCard({
  copy,
  rows,
  isLoading = false,
}: {
  copy: ActivityCardCopy;
  rows: ActivityRowData[];
  /** Suppresses the empty state while the first fetch is in flight. */
  isLoading?: boolean;
}) {
  return (
    <Card as="section" padding="md" aria-label={copy.title}>
      <div className="flex items-center gap-2.5">
        <h2 className="text-[15px] font-semibold text-foreground">{copy.title}</h2>
        <span className="font-num text-xs text-muted-foreground" data-testid="activity-count">
          {rows.length}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{copy.description}</p>

      {rows.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border px-4 py-6 text-center">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">{copy.emptyTitle}</p>
              <p className="mt-1 text-xs text-muted-foreground">{copy.emptyBody}</p>
            </>
          )}
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          {rows.map((row) => (
            <ActivityRow key={row.key} row={row} />
          ))}
        </div>
      )}
    </Card>
  );
}
